import { queryOptions } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import { getPreprIntegrationDetails, getPreprIntegrationList } from "@/api/prepr";
import type { LanguageDto } from "@/api/types";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { QUERY_KEYS } from "@/query-keys";

export const usePortfolioQueries = () => {
  const api = useApi();

  return {
    list: (query?: { date: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_LIST(query?.date),
        queryFn: () =>
          api.getProjectOverviewV1({
            month: query?.date,
            Offset: 0,
            Limit: 1000,
          }),
        staleTime: 1000 * 30,
        select: commonAPIDataSelector,
      }),
    benchmarkStates: ({ date }: { date: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_BENCHMARK(date),
        queryFn: () =>
          api.getProjectOverviewBenchmarkV1({
            month: date,
          }),
        select: commonAPIDataSelector,
      }),
    permissions: () =>
      queryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_PERMISSIONS,
        queryFn: api.getPortfolioPermissionsV1,
        select: commonAPIDataSelector,
        staleTime: 1000 * 60 * 5,
        gcTime: Infinity,
      }),
    availableIntegrationList: (preprUrl: string, languageId: LanguageDto["id"]) =>
      queryOptions({
        queryKey: QUERY_KEYS.AVAILABLE_INTEGRATIONS(languageId),
        queryFn: () => getPreprIntegrationList(preprUrl, languageId),
        select: (response) =>
          response.data.Integrations.items.toSorted((a, b) => a.name?.localeCompare(b.name || "") || -1),
        staleTime: 1000 * 60 * 5,
      }),
    availableIntegrationDetail: (id: string, languageId: LanguageDto["id"], preprUrl: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.AVAILABLE_INTEGRATIONS_DETAIL(id, languageId),
        queryFn: () => getPreprIntegrationDetails(preprUrl, id, languageId),
        staleTime: 1000 * 60 * 5,
      }),
    massAddressesProjects: () =>
      queryOptions({
        queryKey: QUERY_KEYS.BULK_UPLOAD_ADDRESSES_PROJECTS(),
        queryFn: () => api.getMassAddressesProjectsV1(),
        select: commonAPIDataSelector,
      }),
    massAddressesBuildings: () =>
      queryOptions({
        queryKey: QUERY_KEYS.BULK_UPLOAD_ADDRESSES_BUILDINGS(),
        queryFn: () => api.getMassAddressesBuildingsV1(),
        select: commonAPIDataSelector,
      }),
    userProjectsCanInviteList: () =>
      queryOptions({
        queryKey: QUERY_KEYS.PROJECT_INVITE_LIST,
        queryFn: () => api.getUsersMultipleProjectsCanInviteV1(),
        select: (res) => {
          const { projects } = commonAPIDataSelector(res);

          return projects;
        },
      }),
  };
};
